import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Heart, 
  Eye, 
  Brain, 
  Bone, 
  Baby, 
  Stethoscope,
  Smile,
  Activity,
  Syringe,
  Pill
} from 'lucide-react';
import { specialtyService, transformSpecialty } from '@/services/api';

const SpecialtiesPage = () => {
  const { t, i18n } = useTranslation();
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);

  const lang = i18n.language || 'ar';

  const getIconForSpecialty = (name) => {
    const nameLower = name?.toLowerCase() || '';
    if (nameLower.includes('قلب') || nameLower.includes('cardio') || nameLower.includes('heart')) return Heart;
    if (nameLower.includes('عيون') || nameLower.includes('ophthal') || nameLower.includes('eye')) return Eye;
    if (nameLower.includes('عصب') || nameLower.includes('neuro') || nameLower.includes('brain')) return Brain;
    if (nameLower.includes('عظم') || nameLower.includes('ortho') || nameLower.includes('bone')) return Bone;
    if (nameLower.includes('أطفال') || nameLower.includes('pediatr') || nameLower.includes('child')) return Baby;
    if (nameLower.includes('أسنان') || nameLower.includes('dent') || nameLower.includes('tooth')) return Smile;
    if (nameLower.includes('جلد') || nameLower.includes('derma') || nameLower.includes('skin')) return Activity;
    if (nameLower.includes('نسا') || nameLower.includes('gyne') || nameLower.includes('women')) return Syringe;
    if (nameLower.includes('جراح') || nameLower.includes('surg')) return Stethoscope;
    if (nameLower.includes('عام') || nameLower.includes('general')) return Stethoscope;
    return Pill;
  };

  const getColorForSpecialty = (name) => {
    const nameLower = name?.toLowerCase() || '';
    if (nameLower.includes('قلب') || nameLower.includes('cardio')) return 'bg-red-50 text-red-600';
    if (nameLower.includes('عيون') || nameLower.includes('ophthal')) return 'bg-blue-50 text-blue-600';
    if (nameLower.includes('عصب') || nameLower.includes('neuro')) return 'bg-purple-50 text-purple-600';
    if (nameLower.includes('عظم') || nameLower.includes('ortho')) return 'bg-orange-50 text-orange-600';
    if (nameLower.includes('أطفال') || nameLower.includes('pediatr')) return 'bg-pink-50 text-pink-600';
    if (nameLower.includes('عام') || nameLower.includes('general')) return 'bg-teal-50 text-teal-600';
    if (nameLower.includes('أسنان') || nameLower.includes('dent')) return 'bg-cyan-50 text-cyan-600';
    if (nameLower.includes('جلد') || nameLower.includes('derma')) return 'bg-amber-50 text-amber-600';
    if (nameLower.includes('نسا') || nameLower.includes('gyne')) return 'bg-rose-50 text-rose-600';
    if (nameLower.includes('جراح') || nameLower.includes('surg')) return 'bg-indigo-50 text-indigo-600';
    return 'bg-slate-50 text-slate-600';
  };

  useEffect(() => {
    const fetchSpecialties = async () => {
      setLoading(true);
      try {
        const response = await specialtyService.getAll();
        const rawSpecialties =
          response.data?.data?.specialities ||
          response.data?.specialities ||
          response.data?.data ||
          response.data ||
          [];
        const normalizedSpecialties = Array.isArray(rawSpecialties) ? rawSpecialties : [];
        setSpecialties(normalizedSpecialties.map(s => transformSpecialty(s, lang)));
      } catch (error) {
        console.error('Failed to fetch specialties:', error);
        setSpecialties([]);
      } finally {
        setLoading(false);
      }
    };
    fetchSpecialties();
  }, [lang]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="specialties-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-teal-600 to-teal-700 text-white">
        <div className="container mx-auto px-4 py-12">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">{t('nav.specialties')}</h1>
          <p className="text-teal-100 max-w-2xl">
            {lang === 'ar' 
              ? 'تصفح تخصصاتنا الطبية واعثر على الأخصائي المناسب لاحتياجاتك'
              : 'Browse our medical specialties and find the right healthcare professional for your needs'
            }
          </p>
        </div>
      </div>

      {/* Specialties Grid */}
      <div className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Skeleton className="h-14 w-14 rounded-xl" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-5 w-1/2" />
                      <Skeleton className="h-4 w-3/4" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : specialties.length === 0 ? (
          <div className="text-center py-12 text-slate-500 dark:text-slate-400">
            {lang === 'ar' ? 'لا توجد تخصصات متاحة حالياً' : 'No specialties available at the moment'}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {specialties.map((specialty) => {
              const Icon = getIconForSpecialty(specialty.name);
              const colorClass = getColorForSpecialty(specialty.name);
              const hasImage = Boolean(specialty.image);
              
              return (
                <Link 
                  key={specialty.id} 
                  to={`/doctors?specialty=${specialty.id}`}
                  className="group"
                  data-testid={`specialty-card-${specialty.id}`}
                >
                  <Card className="dark:bg-slate-800 border-0 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 h-full">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className={`w-14 h-14 rounded-xl ${colorClass} flex items-center justify-center group-hover:scale-110 transition-transform overflow-hidden`}>
                          {hasImage ? (
                            <img
                              src={specialty.image}
                              alt={specialty.name}
                              className="h-full w-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <Icon className="h-7 w-7" />
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-teal-600 transition-colors">
                            {specialty.name}
                          </h3>
                          {/* Render description as HTML since API returns HTML content */}
                          <div 
                            className="text-sm text-slate-500 mb-2 line-clamp-2"
                            dangerouslySetInnerHTML={{ 
                              __html: specialty.description || `${specialty.name} specialists` 
                            }}
                          />
                          {specialty.doctors_count > 0 && (
                            <span className="text-xs font-medium text-teal-600">
                              {specialty.doctors_count} {t('nav.doctors')}
                            </span>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SpecialtiesPage;
